import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, User, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader } from "../ui";
import { formatCpfCnpj, formatPhone } from "../../lib/masks";

const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

export default function AdminAssociadoNovo() {
  const navigate = useNavigate();
  const [tipos, setTipos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    person_type: "PF",
    name: "",
    email: "",
    password: "",
    phone: "",
    cpf_cnpj: "",
    tipo: "Contribuinte",
    status: "ativo",
    address: "",
    boleto_email: "",
    financial_contact_name: "",
    financial_contact_email: "",
    financial_contact_phone: "",
    legal_rep_name: "",
    legal_rep_email: "",
    legal_rep_phone: "",
  });

  useEffect(() => {
    api
      .get("/admin/associate-types")
      .then((r) => setTipos((r.data || []).filter((t) => t.active)))
      .catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectedTipo = tipos.find((t) => t.name === form.tipo);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("A senha deve ter no mínimo 8 caracteres.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/associados", {
        ...form,
        phone: form.phone || null,
        cpf_cnpj: form.cpf_cnpj || null,
        address: form.address || null,
        boleto_email: form.boleto_email || null,
        financial_contact_name: form.financial_contact_name || null,
        financial_contact_email: form.financial_contact_email || null,
        financial_contact_phone: form.financial_contact_phone || null,
        legal_rep_name: form.legal_rep_name || null,
        legal_rep_email: form.legal_rep_email || null,
        legal_rep_phone: form.legal_rep_phone || null,
      });
      toast.success(`Associado "${r.data.name}" criado com sucesso.`);
      navigate(`/admin/associados/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-novo-associado">
      <button
        onClick={() => navigate("/admin/associados")}
        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-[#002776] mb-4"
      >
        <ArrowLeft size={12} /> Voltar para a lista
      </button>

      <PageHeader
        title="Novo Associado"
        subtitle="Crie o cadastro completo de um associado. As credenciais e os dados para envio de boletos e recibos podem ser preenchidos aqui mesmo."
      />

      <form onSubmit={submit} data-testid="novo-associado-form" className="space-y-6">
        {/* Tipo de Pessoa */}
        <Card className="p-6">
          <h3 className="font-serif-display text-xl mb-4">Tipo de pessoa</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "PF", label: "Pessoa Física", icon: User },
              { v: "PJ", label: "Pessoa Jurídica", icon: Building2 },
            ].map((opt) => {
              const Ic = opt.icon;
              return (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`pt-${opt.v}`}
                  onClick={() => setForm((f) => ({ ...f, person_type: opt.v }))}
                  className={`flex items-center gap-3 px-4 py-4 border-2 rounded-sm text-left ${
                    form.person_type === opt.v
                      ? "border-[#002776] bg-[#002776]/5"
                      : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <Ic size={22} className="text-[#002776]" />
                  <div className="font-medium">{opt.label}</div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Dados principais */}
        <Card className="p-6">
          <h3 className="font-serif-display text-xl mb-4">Dados principais</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>
                {form.person_type === "PJ" ? "Razão Social *" : "Nome completo *"}
              </Label>
              <Input
                testid="na-name"
                required
                minLength={2}
                value={form.name}
                onChange={upd("name")}
              />
            </div>
            <div>
              <Label>E-mail principal *</Label>
              <Input
                testid="na-email"
                type="email"
                required
                value={form.email}
                onChange={upd("email")}
                placeholder="contato@empresa.com.br"
              />
            </div>
            <div>
              <Label>Senha inicial * (mín. 8)</Label>
              <Input
                testid="na-password"
                type="text"
                required
                minLength={8}
                value={form.password}
                onChange={upd("password")}
              />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input
                testid="na-phone"
                value={form.phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))
                }
                placeholder="(11) 99999-9999"
                maxLength={15}
              />
            </div>
            <div>
              <Label>{form.person_type === "PJ" ? "CNPJ" : "CPF"}</Label>
              <Input
                testid="na-doc"
                value={form.cpf_cnpj}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    cpf_cnpj: formatCpfCnpj(e.target.value, form.person_type),
                  }))
                }
                placeholder={
                  form.person_type === "PJ"
                    ? "XX.XXX.XXX/XXXX-XX"
                    : "XXX.XXX.XXX-XX"
                }
                maxLength={form.person_type === "PJ" ? 18 : 14}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Endereço</Label>
              <Input
                testid="na-address"
                value={form.address}
                onChange={upd("address")}
                placeholder="Rua, número, bairro, cidade — UF, CEP"
              />
            </div>
            <div>
              <Label>Tipo de Associado *</Label>
              <select
                data-testid="na-tipo"
                value={form.tipo}
                onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
              >
                {tipos.length === 0 ? (
                  <option value={form.tipo}>{form.tipo}</option>
                ) : (
                  tipos.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.name}
                      {t.monthly_fee > 0
                        ? ` — ${formatBRL(t.monthly_fee)}/mês`
                        : t.annual_fee > 0
                        ? ` — ${formatBRL(t.annual_fee)}/ano`
                        : " — isento"}
                    </option>
                  ))
                )}
              </select>
              {selectedTipo?.description && (
                <p className="text-xs text-slate-500 mt-1">
                  {selectedTipo.description}
                </p>
              )}
            </div>
            <div>
              <Label>Status inicial</Label>
              <select
                data-testid="na-status"
                value={form.status}
                onChange={upd("status")}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
              >
                <option value="ativo">Ativo (acesso imediato)</option>
                <option value="pendente">Pendente (aguarda aprovação)</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Cobrança e financeiro */}
        <Card className="p-6">
          <h3 className="font-serif-display text-xl mb-1">
            Cobrança e departamento financeiro
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Boletos e recibos serão enviados para todos os e-mails preenchidos
            (principal, boletos e financeiro).
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>E-mail para envio de Boletos</Label>
              <Input
                testid="na-boleto-email"
                type="email"
                value={form.boleto_email}
                onChange={upd("boleto_email")}
                placeholder="boletos@empresa.com.br"
              />
            </div>
            <div>
              <Label>Nome do Contato — Financeiro</Label>
              <Input
                testid="na-fin-name"
                value={form.financial_contact_name}
                onChange={upd("financial_contact_name")}
              />
            </div>
            <div>
              <Label>Telefone do Financeiro</Label>
              <Input
                testid="na-fin-phone"
                value={form.financial_contact_phone}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    financial_contact_phone: formatPhone(e.target.value),
                  }))
                }
                placeholder="(11) 99999-9999"
                maxLength={15}
              />
            </div>
            <div className="md:col-span-2">
              <Label>E-mail do Financeiro</Label>
              <Input
                testid="na-fin-email"
                type="email"
                value={form.financial_contact_email}
                onChange={upd("financial_contact_email")}
              />
            </div>
          </div>
        </Card>

        {form.person_type === "PJ" && (
          <Card className="p-6">
            <h3 className="font-serif-display text-xl mb-1">
              Representante Legal
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Obrigatório apenas para Pessoa Jurídica.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nome do Representante Legal</Label>
                <Input
                  testid="na-lr-name"
                  value={form.legal_rep_name}
                  onChange={upd("legal_rep_name")}
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <Label>E-mail do Representante Legal</Label>
                <Input
                  testid="na-lr-email"
                  type="email"
                  value={form.legal_rep_email}
                  onChange={upd("legal_rep_email")}
                />
              </div>
              <div>
                <Label>Telefone do Representante Legal</Label>
                <Input
                  testid="na-lr-phone"
                  value={form.legal_rep_phone}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      legal_rep_phone: formatPhone(e.target.value),
                    }))
                  }
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                />
              </div>
            </div>
          </Card>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={busy}
            data-testid="na-submit"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
          >
            <Save size={14} /> {busy ? "Criando..." : "Criar associado"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/associados")}
            className="px-6 py-3 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
    {children}
  </label>
);

const Input = ({ testid, ...rest }) => (
  <input
    data-testid={testid}
    {...rest}
    className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30 focus:border-[#002776]"
  />
);
